/**
 * Brand constants for code (email HTML, PDF/XLSX renderers). Values mirror assets/tokens/quixo-tokens.json
 * (Quixo Marketing, Editorial Brand System 2026): white ground, true black type, lavender interrupts.
 */
export const BRAND_ID = 'quixo-marketing' as const;
export const BRAND_NAME = 'Quixo Marketing';
/**
 * Legal footer line for every report, report email and template that renders a footer (owner decision 30 Sep 2026).
 * One string, both locales: brand and legal names are not translated. Change it here only.
 */
export const LEGAL_FOOTER = 'Quixo Marketing - Owned and managed by Quixo Hub (quixohub.com)';
/**
 * The one customer-facing support address (owner decision 25 Oct 2026): every customer email, report, footer and help
 * page. It is also the Microsoft Graph sender (GRAPH_SENDER) and Reply-To of customer email. hello@ is kept for
 * outbound sales only and must not appear in customer-facing product copy.
 */
export const SUPPORT_EMAIL = 'support@quixomarketing.com';
/** One support sentence per locale for email footers (plain text; callers escape). */
export const SUPPORT_LINE: Record<'en' | 'ar', string> = {
  en: `Questions? Reply to this email or write to ${SUPPORT_EMAIL}.`,
  ar: `هل لديك سؤال؟ ردّ على هذه الرسالة أو راسلنا على ${SUPPORT_EMAIL}.`,
};
/** Inter Tight (primary), Instrument Serif (editorial pivot), Noto Sans Arabic (Arabic companion). */
export const FONT_STYLESHEET = 'https://fonts.googleapis.com/css2?family=Inter+Tight:wght@400;500;600;700&family=Instrument+Serif:ital@0;1&family=Noto+Sans+Arabic:wght@400;500;600;700&display=swap';

export const colors = {
  /** accent: lavender fills carry black text (11.5:1) */
  hue: '#C7B8EA',
  /** deep lavender: large display only (3.2:1 on white) */
  hueDeep: '#9C83D8',
  hueWash: '#ECE7F8',
  lavender: '#C7B8EA',
  lavenderDeep: '#9C83D8',
  /** portal tones (v2.1, 3 Oct 2026): soft wash, ink for text/focus/small bars (5.3:1 on white), pastel categories */
  lavenderSoft: '#EFEAFB',
  lavenderInk: '#6E56CF',
  coral: '#F39A86',
  sun: '#F2C95C',
  sky: '#9DBDF2',
  mint: '#93D3B0',
  /** true black type */
  ink: '#000000',
  /** secondary text on white (6.9:1) */
  gray: '#5A5A5A',
  /** panels stay white in the editorial system; separation comes from grid hairlines */
  pale: '#FFFFFF',
  /** light panel fill where a surface must read as a block (tables, callouts in PDF) */
  tile: '#F7F7F7',
  /** fine grid lines / rules */
  grid: '#EEEEEE',
  hairline: '#EEEEEE',
  black: '#000000',
  white: '#FFFFFF',
  error: '#B00020',
} as const;

/**
 * Colours for white media (PDF report, emails, XLSX), Editorial 2026: true black text, AA muted gray on white
 * (6.9:1), light editorial panel, #EEEEEE grid hairlines.
 */
export const paper = {
  text: '#000000',
  muted: '#5A5A5A',
  panel: '#F7F7F7',
  hairline: '#EEEEEE',
} as const;

/** Web-safe font stacks for inline email/PDF contexts where web fonts may not load. */
export const fontStacks = {
  display: "'Inter Tight', Helvetica, Arial, sans-serif",
  serif: "'Instrument Serif', Georgia, 'Times New Roman', serif",
  mono: "ui-monospace, 'SFMono-Regular', Menlo, Consolas, 'Courier New', monospace",
  arabic: "'Noto Sans Arabic', Tahoma, Arial, sans-serif",
} as const;

export const assets = {
  lockup: '/brand/lockup/quixo-marketing-horizontal.svg',
  lockupOnBlack: '/brand/lockup/quixo-marketing-horizontal-onblack.svg',
  lockupMono: '/brand/lockup/quixo-marketing-horizontal-mono-black.svg',
  emblem: '/brand/emblem/quixo-marketing.svg',
  favicon: '/favicon/quixo-marketing.svg',
} as const;

export const FAVICON_HEAD = [
  '<link rel="icon" href="/favicon/quixo-marketing.svg" type="image/svg+xml">',
  '<link rel="icon" href="/favicon/quixo-marketing-32.png" sizes="32x32">',
  '<link rel="apple-touch-icon" href="/favicon/quixo-marketing-apple-touch-180.png">',
  '<link rel="manifest" href="/site.webmanifest">',
].join('\n');

/**
 * Inline-safe email styles (Editorial Brand System 2026): white ground, true black type, mono micro-label, tight
 * Inter Tight headline with web-safe fallbacks, black pill CTA, #EEEEEE hairlines, one lavender tick.
 * Physical sides are chosen per direction because many mail clients ignore logical properties.
 */
export function emailStyles(dir: 'ltr' | 'rtl' = 'ltr') {
  const start = dir === 'rtl' ? 'right' : 'left';
  const face = dir === 'rtl' ? fontStacks.arabic : fontStacks.display;
  return {
    body: `margin:0;padding:32px 16px;background:${colors.white}`,
    wrap: `font-family:${face};max-width:560px;margin:0 auto;color:${colors.ink};text-align:${start}`,
    micro: `margin:0 0 32px;font-family:${dir === 'rtl' ? fontStacks.arabic : fontStacks.mono};font-size:11px;line-height:1.4;letter-spacing:${dir === 'rtl' ? '0' : '.16em'};text-transform:uppercase;color:${colors.ink}`,
    tick: `display:block;width:48px;height:4px;margin:0 0 20px;background:${colors.lavender};border-radius:999px;font-size:0;line-height:0`,
    h1: `margin:0 0 16px;font-family:${face};font-size:32px;line-height:1.05;letter-spacing:${dir === 'rtl' ? '0' : '-0.03em'};font-weight:600;color:${colors.ink}`,
    p: `margin:0 0 20px;font-size:16px;line-height:1.6;color:${colors.ink}`,
    cta: `display:inline-block;background:${colors.black};color:${colors.white};border:2px solid ${colors.black};text-decoration:none;font-weight:600;font-size:16px;padding:14px 28px;border-radius:999px`,
    note: `border:1px solid ${colors.grid};border-${start}:4px solid ${colors.lavender};padding:16px 18px;margin:0 0 24px;font-size:15px;line-height:1.6;color:${colors.ink}`,
    footer: `margin:0;font-size:13px;line-height:1.5;color:${colors.gray};border-top:1px solid ${colors.grid};padding-top:16px`,
  } as const;
}
